import clsx from 'clsx';
import { AlertCircle, ArrowUpRight, Building2, Inbox, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Receipt, ShieldCheck, UserRound, Users } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { isDemo } from '@/api';
import { authApi } from '@/api/http';
import { useStudioClients, useStudioOverview, useStudioTeam } from '@/api/studio';
import { Mark } from '@/components/Layout';
import { Avatar } from '@/components/ui';
import { studioCan, studioRoleLabel } from '@/lib/access';
import { isUrgent, urgentCount } from '@/lib/attention';

const SIDEBAR_KEY = 'portal.studio.sidebar';

/** The sidebar folded to icons, remembered per browser. */
function useCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? 'collapsed' : 'open');
    } catch {
      /* not critical */
    }
  }, [collapsed]);
  return [collapsed, () => setCollapsed((c) => !c)];
}

function Item({ to, icon, children, count, end, iconOnly }: { to: string; icon: ReactNode; children: string; count?: number; end?: boolean; iconOnly?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={iconOnly ? children : undefined}
      aria-label={iconOnly ? children : undefined}
      className={({ isActive }) =>
        clsx('relative flex h-8 items-center gap-2.5 rounded text-sm', iconOnly ? 'justify-center' : 'px-2.5', isActive ? 'bg-canvas font-medium shadow-hair' : 'text-muted hover:text-ink')
      }
    >
      <span className="[&>svg]:size-4">{icon}</span>
      {!iconOnly && <span className="flex-1 truncate">{children}</span>}
      {!!count && (iconOnly ? <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-waiting" /> : <span className="num text-xs font-semibold text-waiting">{count}</span>)}
    </NavLink>
  );
}

const logout = () => authApi.logout().finally(() => window.location.assign('/login'));

/**
 * The studio's workspace. Deliberately denser than the client's side: the team
 * works across every client at once, the client only ever sees their own.
 */
export function StudioLayout() {
  const { data: team } = useStudioTeam();
  const { data: overview } = useStudioOverview();
  const { data: clients } = useStudioClients();
  const me = team?.[0];
  const role = me?.studioRole;
  // Only what cannot wait: overdue invoices, payment problems, replies waiting over a day.
  const urgent = urgentCount(overview);
  const replies = overview?.attention.filter((a) => a.kind === 'reply' && isUrgent(a)).length;
  const [menu, setMenu] = useState(false);
  const [collapsed, toggleCollapsed] = useCollapsed();
  const { pathname } = useLocation();
  useEffect(() => setMenu(false), [pathname]);
  const items = (iconOnly = false) => (
    <>
      <Item to="/studio" end icon={<AlertCircle />} count={urgent} iconOnly={iconOnly}>
        Внимание
      </Item>
      <Item to="/studio/inbox" icon={<Inbox />} count={replies} iconOnly={iconOnly}>
        Переписка
      </Item>
      {studioCan(role, 'clients') && (
        <Item to="/studio/clients" end icon={<Building2 />} iconOnly={iconOnly}>
          Клиенты
        </Item>
      )}
      {studioCan(role, 'money') && (
        <Item to="/studio/money" icon={<Receipt />} iconOnly={iconOnly}>
          Счета
        </Item>
      )}
      {studioCan(role, 'team') && (
        <Item to="/studio/team" icon={<Users />} iconOnly={iconOnly}>
          Команда и доступы
        </Item>
      )}
      {studioCan(role, 'team') && (
        <Item to="/studio/admin" icon={<ShieldCheck />} iconOnly={iconOnly}>
          Администрирование
        </Item>
      )}
    </>
  );
  const fold = collapsed ? 'Развернуть меню' : 'Свернуть меню';

  return (
    <div className={clsx('min-h-app md:grid', collapsed ? 'md:grid-cols-[56px_1fr]' : 'md:grid-cols-[240px_1fr]')}>
      {/* On a phone the sidebar is a menu: sections and logout. */}
      <header className="sticky top-0 z-30 border-b border-line bg-canvas/95 backdrop-blur md:hidden">
        <div className="flex h-12 items-center gap-3 px-4">
          <Mark />
          <span className="flex-1 text-sm font-semibold">Кабинет студии</span>
          <button className="btn-ghost size-8 px-0" onClick={() => setMenu((m) => !m)} aria-label="Разделы студии" aria-expanded={menu}>
            <Menu className="size-4" />
          </button>
        </div>
        {menu && (
          <nav className="space-y-0.5 border-t border-line px-2 py-2" aria-label="Студия, меню">
            {items()}
            <Item to="/studio/profile" icon={<UserRound />}>
              Мой профиль
            </Item>
            {!isDemo && (
              <button className="flex h-8 w-full items-center gap-2.5 rounded px-2.5 text-sm text-muted hover:text-ink" onClick={logout}>
                <LogOut className="size-4" /> Выйти
              </button>
            )}
          </nav>
        )}
      </header>
      <aside className="sticky top-0 hidden h-app flex-col border-r border-line bg-panel md:flex">
        <div className={clsx('flex items-center gap-3 py-4', collapsed ? 'flex-col px-2' : 'px-4')}>
          <Mark />
          {!collapsed && (
            <div className="flex-1 leading-tight">
              <div className="text-sm font-semibold">Mark-42</div>
              <div className="text-xs text-muted">Кабинет студии</div>
            </div>
          )}
          <button className="btn-ghost size-8 px-0 text-muted" onClick={toggleCollapsed} aria-label={fold} title={fold} aria-expanded={!collapsed}>
            {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        </div>
        <nav className="space-y-0.5 px-2" aria-label="Студия">
          {items(collapsed)}
        </nav>
        {collapsed ? (
          <div className="flex-1" />
        ) : (
          <div className="mt-6 min-h-0 flex-1 overflow-y-auto px-2">
            <div className="px-2.5 pb-1 text-xs text-faint">Клиенты</div>
            {clients?.map((c) => (
              <NavLink key={c.id} to={`/studio/clients/${c.id}`} className={({ isActive }) => clsx('flex h-8 items-center gap-2 rounded px-2.5 text-sm', isActive ? 'bg-canvas font-medium shadow-hair' : 'text-muted hover:text-ink')}>
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                {c.waitingOnUs > 0 && <span className="size-1.5 rounded-full bg-waiting" aria-label="Ждут ответа студии" />}
              </NavLink>
            ))}
          </div>
        )}
        {collapsed ? (
          <div className="flex flex-col items-center gap-2 border-t border-line py-3">
            {!isDemo && (
              <button className="btn-ghost size-8 px-0 text-muted" onClick={logout} aria-label="Выйти" title="Выйти">
                <LogOut className="size-4" />
              </button>
            )}
            {me && (
              <NavLink to="/studio/profile" title={`Мой профиль: ${me.name}, ${studioRoleLabel[me.studioRole]}`} aria-label="Мой профиль">
                <Avatar person={me} size="sm" />
              </NavLink>
            )}
          </div>
        ) : (
          <div className="space-y-2 border-t border-line p-3">
            {isDemo ? (
              <a href="/" className="flex items-center justify-between rounded px-2.5 py-1.5 text-sm text-muted hover:text-ink">
                Кабинет клиента «Карандаш» <ArrowUpRight className="size-4" />
              </a>
            ) : (
              <button className="flex w-full items-center justify-between rounded px-2.5 py-1.5 text-sm text-muted hover:text-ink" onClick={logout}>
                Выйти <ArrowUpRight className="size-4" />
              </button>
            )}
            {me && (
              <NavLink
                to="/studio/profile"
                aria-label="Мой профиль"
                title="Мой профиль"
                className={({ isActive }) => clsx('flex items-center gap-2.5 rounded px-2.5 py-1', isActive ? 'bg-canvas shadow-hair' : 'hover:bg-canvas')}
              >
                <Avatar person={me} size="sm" />
                <span className="min-w-0 flex-1 truncate text-sm">{me.name}</span>
                <span className="text-xs text-faint">{studioRoleLabel[me.studioRole]}</span>
              </NavLink>
            )}
          </div>
        )}
      </aside>
      <main className="min-w-0 px-4 pb-24 pt-8 md:px-10">
        <div className="mx-auto max-w-[1200px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
